import React, { useMemo } from "react";
import {
     Box,
     Checkbox,
     IconButton,
     Typography,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { formatDate } from "../../../../../../../../../utils/functions";
import { MilestoneTaskItemProps } from "../types";
import {
     getTaskCheckboxState,
     isTaskDisabled,
     isTaskStrikeThrough,
     isTaskIndeterminate,
     shouldShowTaskMenu
} from "../utils/milestoneUtils";
import { milestoneTaskItemStyles } from "./styles/MilestoneTaskItem.styles";

const MilestoneTaskItem: React.FC<MilestoneTaskItemProps> = ({
     task,
     taskState,
     onCheckboxChange,
     onMenuOpen,
}) => {

     const taskStates = useMemo(() => {
          if (!task || !task.divisionTemplate) {
               return {
                    isTaskCompleted: false,
                    isStrikeThrough: false,
                    isIndeterminate: false,
                    showMenu: false,
                    isDisabled: false,
               };
          }
          return {
               isTaskCompleted: getTaskCheckboxState(task),
               isStrikeThrough: isTaskStrikeThrough(task),
               isIndeterminate: isTaskIndeterminate(task),
               showMenu: shouldShowTaskMenu(task),
               isDisabled: isTaskDisabled(task),
          };
     }, [task]);

     const hasCompletionDate = task.milestoneTaskData?.completionDate && task.milestoneTaskData?.completionDate !== null;
     const hasDueDate = task.milestoneTaskData?.dueDate && task.milestoneTaskData?.dueDate !== null;
     const hasDivisionUpdateDate = task.divisionTemplate?.updatedOnDate && task.divisionTemplate?.updatedOnDate !== null;
     const taskName = task.divisionTemplate.taskName;

     return (
          <Box sx={milestoneTaskItemStyles.taskItem}>
               <Box
                    display="flex"
                    alignItems="center"
                    sx={{ flex: 1, overflow: "hidden" }}
               >
                    <Checkbox
                         checked={taskStates.isTaskCompleted}
                         indeterminate={taskStates.isIndeterminate}
                         onChange={(event) => {
                              event.stopPropagation();
                              onCheckboxChange(event);
                         }}
                         sx={milestoneTaskItemStyles.checkbox}
                         disabled={taskStates.isDisabled}
                    />
                    <Typography
                         variant="h1"
                         sx={{
                              textDecoration: taskStates.isStrikeThrough ? "line-through" : "none",
                              wordWrap: "break-word",
                              flex: 1,
                              color: "#5B7282",
                              fontSize: "14px",
                         }}
                    >
                         {taskName}
                    </Typography>
               </Box>

               {(hasCompletionDate && task.milestoneTaskData?.status !== null) || (task.divisionTemplate?.status === false && hasDivisionUpdateDate) ? (
                    <Box
                         data-testid="completion-date-box"
                         display="flex"
                         alignItems="center"
                         sx={{
                              ...(hasCompletionDate && task.milestoneTaskData?.status !== null
                                   ? milestoneTaskItemStyles.completionDate
                                   : taskStates.isStrikeThrough
                                        ? milestoneTaskItemStyles.dueDate
                                        : task.divisionTemplate?.status === false && hasDivisionUpdateDate
                                             ? milestoneTaskItemStyles.divisionUpdateDate
                                             : milestoneTaskItemStyles.completionDate),
                         }}
                    >
                         <CalendarMonthOutlinedIcon fontSize="small" />
                         <Typography variant="body2" sx={{ marginLeft: "4px" }}>
                              {task.divisionTemplate?.status === false && !hasCompletionDate
                                   ? formatDate(task.divisionTemplate?.updatedOnDate!)
                                   : formatDate(task.milestoneTaskData?.completionDate!)
                              }
                         </Typography>
                    </Box>
               ) : hasDueDate ? (
                    <Box data-testid="due-date-box" display="flex" alignItems="center" sx={milestoneTaskItemStyles.dueDate}>
                         <CalendarMonthOutlinedIcon fontSize="small" />
                         <Typography variant="body2" sx={{ marginLeft: "4px" }}>
                              {taskStates.isStrikeThrough ? "" : <span>Due:</span>}{" "}
                              {formatDate(task.milestoneTaskData?.dueDate!)}
                         </Typography>
                    </Box>
               ) : null}

               {taskStates.showMenu && (
                    <IconButton
                         size="small"
                         onClick={onMenuOpen}
                    >
                         <MoreVertIcon sx={{ fontSize: "14px" }} />
                    </IconButton>
               )}
          </Box>
     );
};

export default React.memo(MilestoneTaskItem, (prevProps, nextProps) => {
     // Custom comparison to ensure re-renders when task data changes
     return (
          prevProps.task === nextProps.task &&
          prevProps.taskState === nextProps.taskState &&
          prevProps.onCheckboxChange === nextProps.onCheckboxChange &&
          prevProps.onMenuOpen === nextProps.onMenuOpen
     );
}); 